import React, { useState } from "react";
import styled from "styled-components";
import { mediaQueries } from "../../theme";
import { Flex } from "../Flex";
import { Grid } from "../Grid";
import { DisconnectionFormValues } from "../../types/DisconnectionFormValues";
import lodash from "lodash";
import { useUserAuthentication } from "../../contexts/UserAuthenticationProvider/UserAuthenticationProvider";
import { DisconnectionForm } from "../DisconnectionForm";
import { DisconnectionProgressStepper } from "../DisconnectionProgressStepper";
import { setRequesterDetailsDisconnection } from "../SetRequesterFormDetailsUtil/SetRequesterFormDetailsUtil";
import { EMPTY_UNSUBMITTED_ORDER } from "../../constants/Orders";
import { addUnsubmittedOrderId } from "../AddDraftOrderIdUtil/AddDraftOrderIdUtil";
import {
  deleteUnSubmittedOrder,
  putUnSubmittedOrder,
} from "../../services/OrderService";
import { sendGAEvent } from "../../utils/ga/GaEventSender";
import {
  getGaSaveDraftFailedEvent,
  getGaSaveDraftSuccessEvent,
} from "../../utils/ga/GaLiveningIcpEvents";
import { Redirect, useLocation } from "react-router-dom";
import { BasicNotificationBannerProps } from "../BasicNotificationBanner/BasicNotificationBanner";
import { FloatingBanner } from "../FloatingBanner";
import { submitDisconnectionOrder } from "../../services/SubmitOrderService";
import {
  getGaSubmitFailedEvent,
  getGaSubmitSuccessEvent,
} from "../../utils/ga/GaNewConnectionEvents";
import { useInvalidateOrderList } from "../../hooks/cachedQueries/useInvalidateOrderList/useInvalidateOrderList";
import {
  clearTransactionDetails,
  setTransactionDetails,
} from "../SetTransactionDetailsUtil/SetTransactionDetailsUtil";
import { EMPTY_DISCONNECTION_ORDER } from "../../constants/DisconnectionOrder";

const FlexWrapper = styled(Flex)`
  ${mediaQueries.extraLargeDevice} {
    grid-column-start: 2;
  }
`;

const GridWrapper = styled(Grid)`
  display: flex;
  flex-direction: column;

  ${mediaQueries.extraLargeDevice} {
    display: grid;
    grid-template-columns: 25vw auto 10vw;
  }
`;

interface NotificationBannerProps extends BasicNotificationBannerProps {
  show: boolean;
}

const WrappedNotificationBanner = styled.div`
  position: fixed;
  left: 2.2vh;
  bottom: 14.7vh;
`;

export const Disconnection: React.FC = () => {
  const location = useLocation();
  const { state } = location;

  const {
    authenticatedUserContext: {
      isLoggedIn,
      userBasicDetails,
      userAuth0Details,
    },
  } = useUserAuthentication();
  const { accessToken } = userAuth0Details;
  const { invalidateOrdersCache } = useInvalidateOrderList();

  const isNewOrder = lodash.isEmpty(state);
  const formValues: DisconnectionFormValues = isNewOrder
    ? lodash.cloneDeep(EMPTY_DISCONNECTION_ORDER)
    : state.disconnectionOrder;
  formValues.isUserLoggedIn = isLoggedIn;

  const initialBannerState: NotificationBannerProps = {
    show: false,
    title: "",
    message: "",
    variant: "success",
    icon: "check_circle",
  };

  const [saveOrderResetBanner, setSaveOrderResetBanner] =
    useState(initialBannerState);

  const [activeStep, setActiveStep] = useState(2);
  const [orderDetails, setOrderDetails] = useState<
    DisconnectionFormValues | undefined
  >(undefined);

  const handleStepChange = (activeStep: number) => {
    setActiveStep(activeStep);
  };

  const [unsubmittedOrderId, setUnsubmittedOrderId] = useState(
    isNewOrder ? "" : state.orderId,
  );

  const handleSubmit = async (values: DisconnectionFormValues) => {
    try {
      values.contactId = userBasicDetails?.contactId;
      setTransactionDetails(values);

      await submitDisconnectionOrder(values, accessToken);
      await invalidateOrdersCache();
      await deleteUnSubmittedOrder(unsubmittedOrderId, accessToken);

      setOrderDetails(values);
      sendGAEvent(getGaSubmitSuccessEvent());
    } catch (err: any) {
      setActiveStep(4);
      sendGAEvent(getGaSubmitFailedEvent());
    }
  };

  const handleSave = async (values: DisconnectionFormValues) => {
    let icpOrderSaved;
    try {
      const draftOrder = lodash.cloneDeep(EMPTY_UNSUBMITTED_ORDER);
      draftOrder.orderType = "DisconnectionOrder";
      draftOrder.disconnectionOrder = lodash.cloneDeep(values);
      clearTransactionDetails(draftOrder.disconnectionOrder);

      addUnsubmittedOrderId(unsubmittedOrderId, draftOrder);

      const result = await putUnSubmittedOrder(draftOrder, accessToken);
      setUnsubmittedOrderId(result.orderId);

      icpOrderSaved = true;
      sendGAEvent(getGaSaveDraftSuccessEvent());
    } catch (error) {
      icpOrderSaved = false;
      sendGAEvent(getGaSaveDraftFailedEvent());
    }

    setSaveOrderResetBanner({
      show: true,
      title: icpOrderSaved
        ? "Order saved"
        : "Oops, looks like something went wrong",
      message: icpOrderSaved
        ? "Your order has been successfully saved and can be found in your Draft orders list"
        : "Please wait a few minutes and try again. If the problem persists, contact us at info@vector.co.nz.",
      variant: icpOrderSaved ? "success" : "error",
      icon: icpOrderSaved ? "check_circle" : "warning",
    });
  };

  const handleOnSaveBannerClose = () => {
    setSaveOrderResetBanner({
      show: false,
      title: saveOrderResetBanner.title,
      message: saveOrderResetBanner.message,
      variant: saveOrderResetBanner.variant,
      icon: saveOrderResetBanner.icon,
    });
  };

  return (
    <>
      <DisconnectionProgressStepper currentStep={activeStep} />
      <WrappedNotificationBanner>
        <FloatingBanner
          title={saveOrderResetBanner.title}
          message={saveOrderResetBanner.message}
          variant={saveOrderResetBanner.variant}
          icon={saveOrderResetBanner.icon}
          visible={saveOrderResetBanner.show}
          onBannerClose={handleOnSaveBannerClose}
        />
      </WrappedNotificationBanner>
      <GridWrapper mx={[3, 3, 4, 4, 0]}>
        <FlexWrapper flexDirection="column">
          {!orderDetails ? (
            <DisconnectionForm
              initialValues={setRequesterDetailsDisconnection(
                formValues,
                userBasicDetails,
              )}
              activeStep={activeStep}
              onStepChange={handleStepChange}
              handleSave={handleSave}
              onSubmit={handleSubmit}
            />
          ) : (
            <Redirect
              to={{
                pathname: "/thank-you/disconnection",
                state: orderDetails,
              }}
            />
          )}
        </FlexWrapper>
      </GridWrapper>
    </>
  );
};
